import { isEqual, cloneDeep } from "lodash";

export class Difference {
  message: string;
  original?: SchemaNode;
  other?: SchemaNode;

  constructor(message: string, original?: SchemaNode, other?: SchemaNode) {
    this.message = message;
    this.original = original;
    this.other = other;
  }
}

export interface SchemaNodeKnownProperties {
  minLength?: number;
}

export type SchemaNodeProperties = SchemaNodeKnownProperties & {
  [key: string]: unknown;
};

export const BaseSchema = {
  $schema: "https://json-schema.org/draft/2020-12/schema",
  title: "envelope",
  properties: {
    heading: {
      type: "object",
      additionalProperties: false,
      properties: {},
      required: [
        "release_M3",
        "extended_reference_information_N9",
        "equipment_details_N7_loop",
        "origin_station_F9",
        "destination_station_D9",
        "party_identification_N1_loop",
        "route_information_R2",
        "transaction_set_line_number_LX_loop"
      ]
    }
  },
  type: "object",
  additionalProperties: false,
  required: ["heading"]
} as Record<string, unknown>;

export const nodeTypes = {
  // SchemaNode Types
  Object: "object" as const,
  Array: "array" as const,
  Element: "element" as const
} as const;

export type NodeType = (typeof nodeTypes)[keyof typeof nodeTypes];

export class SchemaNode {
  children: SchemaNode[] = [];
  id: string = generateUUID(); // randomly generated unique identifier for this *instance* of a SchemaNode

  // Base constructor for a SchemaNode
  constructor(
    public type: NodeType, // an element is just an object with a subtype, like "string" or "datetime", or "file_widget"
    public properties: Record<string, unknown> | null,
    public parent?: SchemaNode,
    public label?: string,
    public ediPath?: string, // the full path to this node, before it gets moved around, so like envelope.heading.transaction_set_header_ST.transaction_set_identifier_code_01
    public value?: unknown // the actual value from the original data, if it exists
  ) {
  }

  // toJSON returns the tree as a json object which *should* be json-schema compatible
  // TODO: There's a bug here, where we are mutating the schema nodes after marshalling!
  //  This needs to be fixed!
  toJSON(): Record<string, unknown> {
    // in the case where we are a leaf node, we should just return the value
    switch (this.type) {
      case nodeTypes.Element:
        return elementToJSON(this);
      case nodeTypes.Object:
        return objectToJson(this);
      case nodeTypes.Array:
        return arrayToJson(this);
      default:
    }
    // assume it's an object if it's not got a "type" property, like the envelope
    return objectToJson(this);
  }

  public setEdiPath(path: string): SchemaNode {
    this.ediPath = path;
    return this;
  }

  // Note this does *not* return the stored EDIPath, but instead calculates the dot delineated path to the current node
  // omitting the "envelope".
  public getCurrentPath(): string {
    let currentStr = "";
    let current: SchemaNode = this.clone();
    while (current.parent) {
      if (currentStr == "") {
        currentStr = current.label ? current.label : "unknown";
        current = current.parent;
        continue;
      }
      currentStr = current.label + "." + currentStr;
      current = current.parent;
    }
    return currentStr;
  }

  // getEDIElementName returns the last element of the EDIPath, which is the name of the element
  public getEDIElementName(): string {
    if (!this.ediPath || this.ediPath === "") {
      return "unknown";
    }
    // if the ediPath does not contain a dot, just return it
    if (!this.ediPath.includes(".")) {
      return this.ediPath;
    }
    // just take the EDIPath last index after splitting by a dot
    const split = this.ediPath?.split(".");
    if (!split) {
      return "unknown";
    }
    // return the last element of the split
    return split[split.length - 1];
  }

  // newChild creates a new child node, attaches it to the parent node, and returns the parent.
  public newChild(
    type: NodeType,
    value: Record<string, unknown>,
    label?: string
  ): SchemaNode {
    const newNode = new SchemaNode(type, value, this, label);
    this.children.push(newNode);
    return this;
  }

  // newChildAndReturn creates a new child node, attaches it to the parent node, and returns the child.
  public newChildAndReturn(
    type: NodeType,
    value: Record<string, unknown>,
    label?: string
  ): SchemaNode {
    const newNode = new SchemaNode(type, value, this, label);
    this.children.push(newNode);
    return newNode;
  }

  // newArrayChild attaches a new array child to the parent node, and returns the parent. The arrayProperties
  // get attached to the array, and the itemsProperties get attached to the array.Items property.
  public newArrayChild(
    arrayProperties: Record<string, unknown>,
    itemsProperties: Record<string, unknown>,
    label?: string
  ): SchemaNode {
    const newNode = new SchemaNode(
      nodeTypes.Array,
      arrayProperties,
      this,
      label
    );

    if (newNode.properties) {
      newNode.properties["items"] = { ...itemsProperties };
    }
    this.children.push(newNode);
    return this;
  }

  // addChild adds a child to the node, and returns `this` node, which would be the parent of the child.
  public addChild(child: SchemaNode): SchemaNode {
    child.parent = this;
    this.children.push(child);
    return this;
  }

  // addChildAndReturn adds a child to the node, and returns the child.
  // Note: this is *not* json-schema compliant. This is a helper function for building the tree.
  // If you're trying to build a json-schema compliant child, use addSchemaChildAndReturn
  public addChildAndReturn(child: SchemaNode): SchemaNode {
    child.parent = this;
    this.children.push(child);
    return child;
  }

  // removeChild should remove the relationships between this child and it's parent
  public removeChild(childId: string) {
    // find the child
    const child = this.children.find((child) => child.id === childId);
    if (!child) {
      return;
    }
    // set the child's parent to null
    child.parent = undefined;
    this.children = this.children.filter((child) => child.id !== childId);
    if (this.children.length === 0) {
      this.removeSelf();
    }
  }

  // removeSelf removes this node from the tree, and cleans up the leftover branch
  // that may exist after removing this node.
  public removeSelf() {
    if (this.parent) {
      const parent = this.parent;
      this.parent.removeChild(this.id);
      this.parent = undefined;
      // then walk up the tree, and if the branch is empty, remove it as well
      if (parent.children.length === 0) {
        parent.removeSelf();
      }
    }
  }

  // clone returns a deep copy of the node
  public clone(): SchemaNode {
    const clone = new SchemaNode(
      this.type,
      this.properties,
      this.parent,
      this.label,
      this.ediPath,
      this.value
    );
    for (const child of this.children) {
      const x = child.clone();
      x.parent = clone;
      clone.children.push(x);
    }
    return clone;
  }

  // root returns the top most parent of the node
  public root(): SchemaNode {
    let current: SchemaNode = this;
    while (current.parent) {
      current = current.parent;
    }
    return current;
  }

  // getDesignType tries to determine if the node has a "type" property in the values, which is the json-schema/design type,
  // as opposed to just the node.Type, which is the *node* (internal type), not the type defined by the spec/design.
  public getDesignType(): string {
    if (this.properties && this.properties.type) {
      return this.properties.type as string;
    }
    return "";
  }

  // setDesignType sets the node."tg-design-type" property in the values, which is the json-schema/design type.
  public setDesignType(type: string): SchemaNode {
    if (!this.properties) {
      this.properties = {};
    }
    this.properties["tg-design-type"] = type;
    return this;
  }

  // setDesignValue sets a key in the values, which is the json-schema/design value.
  public setDesignValue(key: string, value: string): SchemaNode {
    if (!this.properties) {
      this.properties = {};
    }
    this.properties[key] = value;
    return this;
  }

  // combine should take in another SchemaNode, and return the composite of the two
  // The other SchemaNode overrides any conflicts in the current SchemaNode
  public combine(other: SchemaNode): SchemaNode {
    // clone current into an output node, so we can CoW this up and make it easier to debug;
    let selfClone = this.clone();
    // in every case, we want to copy over all the values
    for (const key in other.properties) {
      if (selfClone.properties) {
        selfClone.properties[key] = other.properties[key];
      }
    }

    // if the types are arrays, then we just use the other.Children
    if (selfClone.type === nodeTypes.Array) {
      selfClone = combineArrayChildren(selfClone, other);
      return selfClone;
    }
    // if the types are objects, then we can combine them by adding the children of the other node to the current node
    if (
      selfClone.type === nodeTypes.Object ||
      selfClone.type === nodeTypes.Element
    ) {
      // now for each child, we need to recursively combine them
      for (const otherChild of other.children) {
        // check if the child from the other schema exists in this node
        const existingChild = selfClone.children.find(
          (c) => c.label === otherChild.label
        );
        const otherCloneChild = otherChild.clone();
        otherCloneChild.parent = selfClone;
        // if it does not exist, just push it
        if (!existingChild) {
          // cloning does not
          selfClone.children.push(otherCloneChild);
          continue;
        }
        // if it does exist, combine the child.
        // combine the otherClone on top of the existing child, so it overwrites any conflicts
        const combinedChild = existingChild.combine(otherCloneChild);
        // and then set the child in the clone to the combined child
        combinedChild.parent = selfClone;
        selfClone.children = selfClone.children.map((c) => {
          if (c.label === otherChild.label) {
            return combinedChild;
          }
          return c;
        });
      }
    }

    return selfClone;
  }

  // combineExistingOnly copies over properties from the other node when the current
  // node already exists, and the properties are not children EDI segments/loops.
  // This is to support adding overrides, but only when the current node exists to override.
  public combineExistingOnly(other: SchemaNode): SchemaNode {
    // clone current into an output node, so we can CoW this up and make it easier to debug;
    let selfClone = this.clone();
    // in every case, we want to copy over all the values
    // since the current object already exists, then all properties should be copied over too
    for (const key in other.properties) {
      if (selfClone.properties) {
        selfClone.properties[key] = other.properties[key];
      }
    }

    // if the types are arrays, then we just use the other.Children
    // TODO: AUDIT THIS. Is this correct?
    if (selfClone.type === nodeTypes.Array) {
      selfClone = combineArrayChildrenExistingOnly(selfClone, other);
      return selfClone;
    }

    // if the node is not an object or an element, then we can't process it any further
    // so just return it
    if (!(selfClone.type === nodeTypes.Object || selfClone.type === nodeTypes.Element)) {
      return selfClone;
    }

    // if the types are objects, then we can combine them by adding the children of the other node to the current node
    // however in this case we need to check if the object already exists in the current node
    // before adding it.
    // now for every child that exists in self, try to combine it from the other node
    for (const selfChild of selfClone.children) {
      // check if the child from the self schema exists in the other
      const existingOverride = other.children.find(
        (c) => c.label === selfChild.label
      );
      if (existingOverride === undefined) {
        // it's missing from self so don't bother doing anything with it
        continue;
      }
      // if exists in both self and other, so now we need to combine them
      // then clone other child
      const otherCloneChild = existingOverride.clone();

      // if it does exist, combine the children
      const combinedChild = selfChild.combineExistingOnly(otherCloneChild);
      // and then set the newly combined child in the self
      combinedChild.parent = selfClone;
      selfClone.children = selfClone.children.map((c) => {
        if (c.label === combinedChild.label) {
          return combinedChild;
        }
        return c;
      });
    }

    return selfClone;
  }

  // equals should take in another SchemaNode, and return true if they are equal, false otherwise
  public equals(other: SchemaNode): boolean {
    // not necessarily true, because we sometimes set an "object" to a "element" or "segment" etc
    // if (this.Type !== other.Type) {
    //     return false;
    // }
    if (this.label !== other.label) {
      return false;
    }
    // compare values, ignoring the prototype
    // TODO: is there a better way to do this?
    if (JSON.stringify(this.properties) !== JSON.stringify(other.properties)) {
      return false;
    }
    if (this.children.length !== other.children.length) {
      return false;
    }
    const diffs = this.compareChildren(other, []);

    return diffs.length === 0;
  }

  // compare is a hastily written function that compares two SchemaNodes and returns a list of differences
  public compare(other: SchemaNode): Difference[] {
    const differences: Difference[] = [];
    // if the types are different, then we can't compare them
    // if (this.Type !== other.Type) {
    //     throw "Can't compare different types!";
    // }
    if (this.label !== other.label) {
      differences.push(new Difference("label mismatch", this, other));
      return this.compareChildren(other, differences);
    }
    // compare values, ignoring the prototype
    // TODO: is there a better way to do this?

    // if (!deepEqual(this.properties, other.properties, new Set(["prototype"]))) {
    //   differences.push(new Difference("value mismatch", this, other));
    //   return this.compareChildren(other, differences);
    // }
    // if (this.children.length !== other.children.length) {
    //   differences.push(new Difference("children length mismatch", this, other));
    //   const diffs = this.compareChildren(other, differences);
    //   return diffs;
    // }
    return this.compareChildren(other, differences);
  }

  // compareChildren is a helper function for the comparison, handling the unique structure of the tree
  compareChildren(other: SchemaNode, differences: Difference[]): Difference[] {
    // for each child in self, check if the same child exists in other based on label
    // if it does, compare them
    // if it doesn't, add it to the differences
    for (const child of this.children) {
      const otherChild = other.children.find((c) => c.label === child.label);
      if (!otherChild) {
        differences.push(new Difference("other is missing child", child));
        continue;
      }
      // save it so we can inspect in debug mode
      const newDifferences = child.compare(otherChild);
      differences.push(...newDifferences);
    }
    // now for each child in other, if it doesn't exist in self, add it to the differences
    for (const child of other.children) {
      const selfChild = this.children.find((c) => c.label === child.label);
      if (!selfChild) {
        differences.push(
          new Difference("self is missing child", undefined, child)
        );
      }
    }
    return differences;
  }
}

function generateUUID(): string {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function(c) {
    const r = (Math.random() * 16) | 0,
      v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// so equal it's deep
function deepEqual(
  x?: Record<string, unknown> | null,
  y?: Record<string, unknown> | null,
  ignoreRootProps?: Set<string>
) {
  if (x == null || y == null) return x === y;

  const xkeys = Object.keys(x).sort();
  const ykeys = Object.keys(y).sort();
  if (!isEqual(xkeys, ykeys)) {
    return false;
  }

  for (const key of xkeys) {
    if (ignoreRootProps && ignoreRootProps.has(key)) continue;
    if (!isEqual(x[key], y[key])) {
      return false;
    }
  }
  return true;
}

// For marshalling when the node.Type is an "element", which is a specific input
function elementToJSON(node: SchemaNode): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  // for every property in node.Value, add it to the result
  for (const key in node.properties) {
    result[key] = node.properties[key];
  }
  if (node.ediPath) {
    result.ediPath = node.ediPath;
  }

  if (node.properties) {
    result.type = node.properties.type;
  }

  if (node.value !== undefined) {
    result.value = node.value;
  }
  return result;
}

// For marshalling when the node.Type is "object", which is a collection that has children, so requires recursion
function objectToJson(node: SchemaNode): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const key in node.properties) {
    result[key] = node.properties[key];
  }
  if (node.ediPath) {
    result.ediPath = node.ediPath;
  }
  // now add a "properties" key to the result, since this is an object (and not an array)
  result["properties"] = {};
  const properties: Record<string, unknown> = result["properties"] as Record<
    string,
    unknown
  >;
  // for every child in the node, add it to the result
  for (const child of node.children) {
    const key = child.label ? child.label : "";
    // TDOO: protect against duplicate keys?
    properties[key] = child.toJSON();
  }
  return result;
}

function arrayToJson(node: SchemaNode): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const key in node.properties) {
    result[key] = node.properties[key];
  }
  if (node.ediPath) {
    result.ediPath = node.ediPath;
  }
  if (node.value !== undefined) {
    result.value = node.value;
  }
  // this assumes that the node.Value[key] contains an "items" key that also contains various properties from the original
  // schema, and a blank "properties" key, which will be filled in by the children
  const items = result["items"] as Record<string, unknown>;

  items["properties"] = {};
  const properties: Record<string, unknown> = items["properties"] as Record<
    string,
    unknown
  >;
  // for every child in the node, add it to the result
  for (const child of node.children) {
    const key = child.label ? child.label : "";
    // TDOO: protect against duplicate keys?
    properties[key] = child.toJSON();
  }
  return result;
}

// getPropertiesAtPath returns the properties at the given path, which is a dot delimited string
// If the object cannot be found, an empty object is returned
export function getPropertiesAtPath(
  node: SchemaNode,
  path: string
): Record<string, unknown> {
  const target = getSchemaNodeAtPath(node, path);
  if (!target) {
    return {};
  }
  if (!target.properties) {
    return {};
  }
  return target.properties;
}

// newEmptySchema returns a new json-schema object, as expected by json-schema for a 404/tender document.
// It contains a "heading" child, which is the heading section in a 404 document. This *does not work* for
// other message types that use a different name for this "heading".
// Furthermore, this returns the child leaf that new children should be attached to.
export function newEmptyTenderSchema(): SchemaNode {
  const graph = createTree(BaseSchema);
  if (!graph) {
    throw new Error("Could not create a new empty schema");
  }
  // walk to envelope.heading
  const heading = getSchemaNodeAtPath(graph, "envelope.heading");
  if (!heading) {
    throw new Error("Could not find heading in the schema");
  }
  return heading;
}

// createTree assumes data is an entire json-schema, or the object behind the result of a SchemaNode.properties[value]
// This practically means it can be anything that has a schema type of "object",
// or any of the SchemaNode.Type == "element" that also have SchemaNode.Value.type of "string", "integer", or "number".
export function createTree(data: Record<string, unknown>): SchemaNode | null {
  // createNode creates a root node, so the value should be a json-schema object
  if (data === null) {
    return null;
  }
  // if data contains no keys, then it's not a valid json-schema
  if (Object.keys(data).length === 0) {
    throw new Error("data is an empty object");
  }

  // set default rootNode value
  // detect if the data[title] exists. If it does, then we are at the root of a schema
  const rootNode: SchemaNode = new SchemaNode(
    nodeTypes.Object,
    null,
    undefined,
    data.title ? (data.title as string) : "",
    data.title ? (data.title as string) : ""
  );

  // the data of the node is a map of all properties, minus the "properties" or "items" key,
  // so now copy all the properties into the root node
  rootNode.properties = prepareValue(data);

  // set the path for this property
  rootNode.setEdiPath("");

  // detect if this is an element, which means it:
  // is an object
  // has a type of "string", "integer", "number"
  // does not have a "properties" key
  if (isElement(data)) {
    rootNode.type = nodeTypes.Element;
    return rootNode;
  }

  // detect if it's an array, which means that:
  // it is an object
  // has the type of "array"
  // does not have a properties key
  if (isArray(data)) {
    rootNode.type = nodeTypes.Array;
    // now we need to add all the properties from the rootNode.items to the Value, minus the "properties" key
    rootNode.properties["items"] = prepareValue(
      data.items as Record<string, unknown>
    );
  }

  // now call buildChildren on data.properties or data.items.properties, if it exists
  const next = getNextChildren(data);
  const children = buildChildren(rootNode, next);
  if (children) {
    rootNode.children = children;
  }

  return rootNode;
}

export function createTreeWithDefaultRoot(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: Record<string, any>
): SchemaNode {
  // append the object.keys to the BaseSchema.properties, deep copying
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const base: any = cloneDeep(BaseSchema);

  base.properties.heading.properties = {
    ...base.properties.heading.properties,
    ...data
  };
  const graph = createTree(base);
  if (!graph) {
    throw new Error("Could not create a new empty schema");
  }
  return graph;
}

export function decorateTreeWithPaths(node: SchemaNode): SchemaNode {
  const current = node.root();
  let toSearch: SchemaNode[] = node.children;
  // if we're at the envelope/root, we should walk to the next child before trying to start
  if (node.label === "envelope") {
    // reset toSearch, because we don't want to search the envelope
    toSearch = [];
    for (const child in node.children) {
      toSearch.push(...node.children[child].children);
    }
  }

  current.setEdiPath(node.label ? node.label : "root");
  let label = node.label ? node.label : "root";
  // if we're at the envelope, we don't want to prefix with that label
  if (label == "envelope") {
    label = "";
  }

  for (const child of toSearch) {
    decorateTreeWithPathsHelper(child, label);
  }

  return current.root();
}

function decorateTreeWithPathsHelper(node: SchemaNode, path: string) {
  let nextPath: string;
  if (path === "") {
    nextPath = node.label ? node.label : "unknown";
  } else {
    nextPath = path + "." + (node.label ? node.label : "unknown");
  }
  node.setEdiPath(nextPath);
  for (const child of node.children) {
    decorateTreeWithPathsHelper(child, node.ediPath ? node.ediPath : "");
  }
}

// getNextChildren chooses the next children to walk. If it's an array, the children exist in items.properties,
// if it's an object, the children exist in properties
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getNextChildren(data: any) {
  // if it's not an object with keys, throw an error
  if (typeof data !== "object") {
    throw new Error("data is not an object");
  }
  if (data.properties) {
    return data.properties;
  } else if (data.items && data.items.properties) {
    return data.items.properties;
  } else {
    throw new Error("Neither data.properties nor data.items.properties exist");
  }
}

// isElement detects if the current data object is an element in the json-schema/EDI sense. Any form input (like heading.equipment_details_N7_loop.equipment_details_N7.equipment_number_02)
// is an element.
function isElement(data: Record<string, unknown>): boolean {
  if (!data.type) {
    return false;
  }
  if (data.properties) {
    return false;
  }
  switch (data.type) {
    case "string":
    case "integer":
    case "number":
      return true;
  }

  return false;
}

// isArray detects if the current data object is an array in the json-schema/EDI sense. It represents a segment that can be listed several times.
function isArray(data: Record<string, unknown>): boolean {
  if (!data.type) {
    return false;
  }
  if (data.type !== "array") {
    return false;
  }
  return !data.properties;
}

/*
    createNodeHelper should take in a parent node, and the value for the next node
    The 'value' should be a json-schema object/array.
    If it's an array, it's something like a "required" array:
             "required": [
                "transaction_set_identifier_code_01",
                "transaction_set_control_number_02"
              ]
    If it's an object, it represents 1 of 3 things:
    1. An array declaration in a json-schema. This is defined by it being an object with a "type" property with the value "array"
     These "arrays" contain an "items" property which contains the definitions of what segments are available in this loop
    2. An object which represents a segment in an EDI document. For example, "transaction_set_header_ST" is a segment, and it would have a "type" property of "object"
     These "objects" contain a "properties" property, which defines all the elements in that segment
    3. An object which represents the desired validation/type information for an element. Here's an example:
                "transaction_set_identifier_code_01": {
                  "type": "string",
                  "default": "404",
                  "enum": [
                    "404"
                  ],
                  "x12-codes": {
                    "404": {
                      "code": "404"
                    }
                  }
                },
    which defines the TS identifier code which is an element of the transaction_set_header_ST segment
    This function does not mutate the parent node, so the caller should add the result to the parent.Children
*/
export function createNodeHelper(
  parent: SchemaNode,
  value: Record<string, unknown>
): SchemaNode {
  // detect if the value is an array
  if (Array.isArray(value)) {
    // make a new Node that contains all the values of the array as the value
    const newNode = new SchemaNode(
      nodeTypes.Array,
      prepareValue(value),
      parent
    );
    parent.children.push(newNode);
    return newNode;
  }

  // detect if the value is an object
  if (!(typeof value === "object")) {
    throw "invalid value type, we must've gone too far! pull back!";
  }

  // check if the object is an array
  if (value.type && value.type === nodeTypes.Array) {
    // make a new Node that contains the items as the value
    const node: SchemaNode = new SchemaNode(
      nodeTypes.Array,
      prepareValue(value),
      parent
    );
    // also need to add the value.items properties, excluding the "properties" key
    if (node.properties) {
      node.properties["items"] = prepareValue(
        value.items as Record<string, unknown>
      );
    }
    // create the children of the node
    const items = value.items as Record<string, unknown>;
    node.children = buildChildren(node, items.properties);
    parent.children.push(node);
    return node;
  }

  // make a new Node that contains the properties as the value
  const node: SchemaNode = new SchemaNode(
    nodeTypes.Object,
    prepareValue(value),
    parent
  );

  // if this object is an element (string, integer, float, enum, etc.), then we need to change the type to "element".
  if (value.type && value.type !== nodeTypes.Object) {
    parent.children.push(node);
    node.type = "element";
    // we also don't have to build the children, because this is the end of the line
    return node;
  }

  // create the children of the node
  parent.children.push(node);
  node.children = buildChildren(node, value.properties);
  return node;
}

export function getObjectAtPath(
  data: Record<string, unknown>,
  path: string
): Record<string, unknown> | null {
  const splitPath = path.split(".");
  let current = data;
  for (const key of splitPath) {
    if (current[key] === undefined) {
      return null;
    }
    current = current[key] as Record<string, unknown>;
  }
  return current;
}

// buildSchemaAtPath should take in a reference schema, the current "working" schema being built, and the path
// a new object that should be added to the referenceSchema. The result is the referenceSchema with the new object added
export function buildSchemaAtPath(
  referenceSchema: SchemaNode,
  schemaBeingBuilt: SchemaNode,
  path: string
): SchemaNode {
  // const startTime = new Date().getTime();
  // console.log("BuildSchmeaAtPath at " + startTime.toString());
  // get the node at the path
  const referenceNode = getSchemaNodeAtPath(referenceSchema, path);
  if (!referenceNode) {
    throw `Could not find node at path: ${path}`;
  }
  // also check if the current node already exists in the schemaBeingBuilt, if so just return it

  const existingNode = getSchemaNodeAtPath(schemaBeingBuilt, path);
  if (existingNode) {
    return schemaBeingBuilt;
  }

  // clone so we can moo like a CoW and only operate on the clone
  const clone = schemaBeingBuilt.clone();

  // now build all intermediate nodes, from the top down
  let splitPath = path.split(".");
  let currentSchemaNode: SchemaNode = referenceSchema.root();
  let currentCloneNode: SchemaNode = clone.root();

  // we need to handle the special case where the key *does not* contain the section prefix, but the actual target does
  const actualTargetPath = referenceNode.getCurrentPath();
  const splitActualTargetPath = actualTargetPath.split(".");
  if (splitActualTargetPath.length > splitPath.length) {
    splitPath = splitActualTargetPath;
  }

  // for each key in the path, make sure it exists in the clone
  // if it does, then merge the two nodes
  // if not, build it and move on to the next child
  for (const key of splitPath) {
    // get the reference node at the key
    const referenceChild = getSchemaNodeAtPath(currentSchemaNode, key);
    if (!referenceChild) {
      throw "Could not find child in reference schema!";
    }
    // get the clone node at the key
    let cloneChild = getSchemaNodeAtPath(currentCloneNode, key);
    if (!cloneChild) {
      // if the clone child doesn't exist, then we need to build it
      cloneChild = referenceChild.clone();
      cloneChild.children = [];
      cloneChild.parent = currentCloneNode;
      currentCloneNode.children.push(cloneChild);
    } else {
      // if the clone child does exist, then we need to merge the two nodes
      const cloneReferenceChild = referenceChild.clone();
      // now set the clone children to an empty set, b/c we don't want to add children yet, it will be merged in later keys/paths
      cloneReferenceChild.children = [];
      // combine the cleaned node to the current cloned child
      cloneChild = cloneChild.combine(cloneReferenceChild);
      // set the cloned child parent to the new graph parent, only if we're not at the root
      if (cloneChild.parent) {
        cloneChild.parent = currentCloneNode;
      }
      // now add cloneChild as the child of the currentCloneNode
      currentCloneNode.children = currentCloneNode.children.reduce(
        (acc, child) => {
          if (cloneChild && child.label === key) {
            acc.push(cloneChild);
          } else if (child !== null) {
            acc.push(child);
          }
          return acc;
        },
        [] as SchemaNode[]
      );
    }
    currentSchemaNode = referenceChild;
    currentCloneNode = cloneChild;
  }

  // const endTime = Date.now();
  // const timeTaken = (endTime - startTime) / 1000;
  // if (timeTaken > 1.1) {
  //     console.log(`Finished buildSchemaAtPath at ${endTime.toString()}, time taken: ${timeTaken} seconds`);
  // }

  // return the leaf node
  return currentCloneNode as SchemaNode;
}

// PRIVATE FUNCTIONS, THERE'S NOTHING INTERESTING HAPPENING HERE

// prepareValue copies over all properties from within an object, except for "properties" and "items"
function prepareValue(data: Record<string, unknown>): Record<string, unknown> {
  const value = {} as Record<string, unknown>;
  for (const key in data) {
    if (key !== "properties" && key !== "items") {
      value[key] = data[key];
    }
  }
  return value;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function buildChildren(parent: SchemaNode, value: any): SchemaNode[] {
  if (typeof value === "object") {
    if (Array.isArray(value)) {
      const nodes: SchemaNode[] = [];
      for (const child of value) {
        nodes.push(createNodeHelper(parent, child));
      }
      return nodes;
    }
    const nodes: SchemaNode[] = [];
    for (const [key, child] of Object.entries(value)) {
      // if the value[key] is not an object, then it's a value we just want to add
      // like an array of strings, a string, float, or an integer
      if (typeof value[key] !== "object") {
        const newNode = new SchemaNode("element", value[key], parent, key);
        nodes.push(newNode);
        continue;
      }
      // otherwise recursively build the children
      const newChild = createNodeHelper(
        parent,
        child as Record<string, unknown>
      );
      newChild.label = key;
      nodes.push(newChild);
    }
    return nodes;
  }
  return [];
}

// source should be an object that has a property "type" which has a value of "array"
function combineArrayChildren(
  source: SchemaNode,
  other: SchemaNode
): SchemaNode {
  const result: SchemaNode = source.clone();
  // for every child in other, if it *does not* exist in source, then add it
  for (const child of other.children) {
    const otherChild = result.children.find((c) => c.label === child.label);
    if (!otherChild) {
      const newChild = child.clone();
      child.parent = result;
      result.children.push(newChild);
      continue;
    }
    const combinedChild = child.combine(otherChild);
    result.children = result.children.map((c) => {
      if (c.label === child.label) {
        return combinedChild;
      }
      return c;
    });
  }
  return result;
}

// this is the same as combineArrayChildren, but only combines if source contains other
function combineArrayChildrenExistingOnly(
  source: SchemaNode,
  other: SchemaNode
): SchemaNode {
  const result: SchemaNode = source.clone();
  // for every child in other, if it *does not* exist in source, then add it
  for (const child of other.children) {
    const sourceChild = result.children.find((c) => c.label === child.label);
    if (!sourceChild) {
      continue;
    }
    const combinedChild = sourceChild.combineExistingOnly(child);
    result.children = result.children.map((c) => {
      if (c.label === child.label) {
        return combinedChild;
      }
      return c;
    });
  }
  return result;
}

export function getNestedProperty<T>(
  obj: Record<string, unknown>,
  keys: string[]
): T | undefined {
  if (keys.length === 0) {
    return obj as T;
  }

  const key = keys.shift();
  if (!key) {
    return undefined;
  }

  const nested = obj[key];

  if (keys.length === 0) {
    return nested as T;
  }

  if (typeof nested !== "object") {
    return undefined;
  }

  return getNestedProperty(nested as Record<string, unknown>, keys);
}

// getSchemaNodeAtPath should take in a SchemaNode and a path, and return the SchemaNode at that path
// paths are expected to be a dot delimited set of keys. No arrays should be traversed, so that is not supported
// This path *should not* include the "envelope" or the "heading"/"detail" sections as part of the path. They
// are assumed, and all sections are searched for the path. However, the root of the schemaNode assumes the "envelope"
// and other sections. This function returns null for all errors!
// stopAtArray will stop walking the tree and return if the current node is an array. This is useful for the hydration
// step, where we don't want to walk the children of an array, since the entire object is set as the value of the array
// WARNING: THE BEHAVIOR OF THIS FUNCTION IS TIGHTLY COUPLED TO THE LOGIC IN THIS APPLICATION.
//  ALTER AT YOUR OWN RISK
export function getSchemaNodeAtPath(
  node: SchemaNode,
  path: string,
  stopAtArray: boolean = false
): SchemaNode | null {
  // we need to be smart about walking the envelope. It would be nice to just specify
  // heading.transaction_set_header_ST.transaction_set_identifier_code_01, and not have to worry about the envelope,
  // so we need to be able to walk the envelope if we need to
  const splitPath = path.split(".");

  // if the len of splitPath is 0 now, then we just specified "envelope" so we should return the envelope
  if (splitPath.length === 0) {
    return node;
  }

  // add the current children as part of the search list
  let toSearch: SchemaNode[] = [...node.children];

  // if the target is the envelope, that's it, then return the envelope
  if (splitPath[0] === "envelope" && splitPath.length === 1) {
    if (node.label === "envelope") {
      return node;
    }
    return null;
  }

  // in every other case, we want to strip the "envelope" from the path, because
  // it is automatically assumed
  if (splitPath[0] === "envelope") {
    splitPath.shift();
  }

  if (node.type === nodeTypes.Array && stopAtArray) {
    return node;
  }

  // if the node is the root/envelope, automatically add the appropriate children
  if (node.label === "envelope") {
    // we are in root mode, so automatically eat the root nad the sections
    for (const section in node.children) {
      // we want to add the children of the envelope.<section>, so it's the root.Children[section].Children
      toSearch.push(...node.children[section].children);
    }
  }

  if (toSearch.length === 0) {
    return null;
  }

  // now walk the children of the node to find the desired node
  let current: SchemaNode = node;
  for (const key of splitPath) {
    const child = toSearch.find((child) => child.label === key);
    if (!child) {
      return null;
    }
    current = child;
    // now if we're at an array, and we're supposed to stop at an array, then we should return the current node
    if (current.type === nodeTypes.Array && stopAtArray) {
      return current;
    }
    toSearch = [...current.children];
  }
  return current as SchemaNode;
}
